import { useState, useEffect } from "react";
import { ChevronLeft, MapPin, Copy, Check, Phone, Mail, ExternalLink, Home as HomeI, Compass, MessageCircle, Lock } from "lucide-react";
import { themeTokens } from "@/lib/sections";
import { Icon, sectionVisible, sortSections } from "@/lib/guideTemplate";
import { BlockRenderer } from "@/components/BlockRenderer";
import { ConciergeHomeCard } from "@/components/ConciergeChat";
import { MobilityBlock } from "@/components/MobilityBlock";
import api, { mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { LogoSymbol } from "@/components/Brand";

export function GuestGuideView({ guide, lang = "en", bottomNav = false }) {
  const [active, setActive] = useState(null);
  const [phase, setPhase] = useState("before");
  const [copied, setCopied] = useState("");
  const [unlocked, setUnlocked] = useState(null);
  const [pinOpen, setPinOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [pinErr, setPinErr] = useState("");
  const [pinBusy, setPinBusy] = useState(false);
  const slug = guide?.slug;
  const protection = guide?.access_protection;
  const T = themeTokens(guide?.branding?.theme, guide?.branding?.brand_color);
  const brand = T.accent;
  const t = guide?.translations?.[lang] || {};
  const isBase = lang === (guide?.base_language || "en");
  const tr = (v, k) => (isBase ? v : (t[k] ?? v));
  const surface = { background: T.surface, borderColor: T.border };
  const label = { color: T.muted };
  const text = { color: T.text };
  const bodyCol = { color: T.dark ? T.muted : "#4B5563" };
  const cover = mediaUrl(guide?.branding?.cover) || "https://images.unsplash.com/photo-1631679706909-1844bbd07221?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

  const sections = sortSections(guide?.sections).filter(sectionVisible);
  const byKey = (k) => sections.find((s) => s.key === k);
  const PHASE_ORDER = {
    before: ["checkin", "getting_around", "access", "wifi", "parking", "house_rules", "contact"],
    during: ["wifi", "house_guide", "house_rules", "getting_around", "parking", "emergency", "contact"],
    checkout: ["checkout", "waste", "house_rules", "contact", "emergency"],
  };
  const PHASES = [["before", "Prima dell'arrivo"], ["during", "Durante il soggiorno"], ["checkout", "Check-out"]];
  const orderByPhase = (list) => {
    if (!guide?.smart_journey) return list;
    const pr = PHASE_ORDER[phase] || [];
    const rank = (k) => { const i = pr.indexOf(k); return i === -1 ? 999 : i; };
    return [...list].sort((a, b) => rank(a.key) - rank(b.key));
  };
  const copy = (v, k) => { navigator.clipboard?.writeText(v); setCopied(k); setTimeout(() => setCopied(""), 1500); };

  useEffect(() => {
    if (!slug || !protection?.available) return;
    let token; try { token = sessionStorage.getItem(`sg_guest_${slug}`); } catch (e) { /* noop */ }
    if (!token) return;
    api.get(`/public/guide/${slug}/protected`, { headers: { "X-Guest-Token": token } })
      .then(({ data }) => setUnlocked(data.protected))
      .catch(() => { try { sessionStorage.removeItem(`sg_guest_${slug}`); } catch (e) { /* noop */ } });
  }, [slug, protection?.available]);

  const verifyPin = async () => {
    if (pinBusy || pin.length < 4) return;
    setPinBusy(true); setPinErr("");
    try {
      const { data } = await api.post(`/public/guide/${slug}/verify-pin`, { pin });
      try { sessionStorage.setItem(`sg_guest_${slug}`, data.token); } catch (e) { /* noop */ }
      const prot = await api.get(`/public/guide/${slug}/protected`, { headers: { "X-Guest-Token": data.token } });
      setUnlocked(prot.data.protected);
      setPinOpen(false); setPin("");
    } catch (e) {
      setPinErr(e.response?.status === 429
        ? "Troppi tentativi. Riprova tra qualche minuto."
        : (typeof e.response?.data?.detail === "string" ? e.response.data.detail : "PIN non corretto."));
    } finally { setPinBusy(false); }
  };

  const acc = {
    keybox: unlocked?.access?.keybox ?? guide?.access?.keybox,
    access_codes: unlocked?.access?.access_codes ?? guide?.access?.access_codes,
  };
  const wifiPass = unlocked?.wifi_password ?? guide?.wifi?.password;
  const accessLocked = !!protection?.access && !unlocked;
  const wifiLocked = !!protection?.wifi_password && !unlocked;

  const Locked = ({ title }) => (
    <button onClick={() => { setPinErr(""); setPinOpen(true); }} data-testid="protected-unlock"
      className="w-full rounded-xl border p-3.5 flex items-center justify-between gap-3 text-left active:scale-[0.98] transition-transform" style={surface}>
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="w-8 h-8 rounded-lg grid place-items-center shrink-0" style={{ background: `${brand}22`, color: brand }}><Lock className="w-4 h-4" /></div>
        <div className="min-w-0"><p className="text-[11px] font-bold uppercase tracking-wide" style={label}>{title}</p><p className="text-[13px]" style={text}>Informazioni protette</p></div>
      </div>
      <span className="text-xs font-semibold shrink-0" style={{ color: brand }}>Visualizza</span>
    </button>
  );

  const requestService = (svc) => {
    const c = guide?.contact || {};
    const text = `Ciao! Vorrei richiedere: ${svc.title}${svc.price ? ` (${svc.price})` : ""}. Grazie!`;
    const wa = (c.whatsapp || "").replace(/[^0-9]/g, "");
    if (wa) return window.open(`https://wa.me/${wa}?text=${encodeURIComponent(text)}`, "_blank");
    if (c.email) return window.open(`mailto:${c.email}?subject=${encodeURIComponent("Richiesta servizio")}&body=${encodeURIComponent(text)}`, "_blank");
    if (c.phone) return window.open(`sms:${c.phone}?&body=${encodeURIComponent(text)}`, "_blank");
    toast.info("Contatta l'host per richiedere questo servizio");
  };

  const Info = ({ k, v, mono }) => v ? (
    <div className="rounded-xl border p-3.5" style={surface}><p className="text-[11px] font-bold uppercase tracking-wide" style={label}>{k}</p><p className={`text-[15px] mt-0.5 whitespace-pre-wrap ${mono ? "font-mono" : ""}`} style={text}>{v}</p></div>
  ) : null;
  const Pill = ({ k, v }) => v ? <div className="inline-flex items-center gap-2 rounded-full border px-4 py-2" style={surface}><span className="text-xs" style={label}>{k}</span><span className="font-semibold" style={text}>{v}</span></div> : null;
  const Call = ({ k, v }) => v ? <a href={`tel:${v}`} className="rounded-xl border p-3.5 flex items-center justify-between" style={surface}><div><p className="text-[11px] font-bold uppercase tracking-wide" style={label}>{k}</p><p className="text-[15px] mt-0.5" style={text}>{v}</p></div><Phone className="w-4 h-4 text-emerald-500" /></a> : null;
  const CopyRow = ({ k, v, ck, mono }) => v ? <div className="rounded-xl border p-3.5 flex items-center justify-between gap-3" style={surface}><div className="min-w-0"><p className="text-[11px] font-bold uppercase tracking-wide" style={label}>{k}</p><p className={`text-[15px] mt-0.5 truncate ${mono ? "font-mono" : ""}`} style={text}>{v}</p></div><button onClick={() => copy(v, ck)} style={label}>{copied === ck ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}</button></div> : null;

  const structured = (s) => {
    const k = s.key, w = guide;
    switch (k) {
      case "welcome": return <p className="text-[15px] leading-relaxed whitespace-pre-wrap" style={bodyCol}>{tr(w.welcome_message, "welcome_message")}</p>;
      case "checkin": return <div className="space-y-3"><Pill k="Check-in dalle" v={w.checkin?.time} /><P v={tr(w.checkin?.instructions, "checkin_instructions")} /><Info k="Porta" v={w.access?.door_instructions} />{accessLocked ? <Locked title="Accesso protetto" /> : <><Info k="Keybox" v={acc.keybox} /><Info k="Codice" v={acc.access_codes} mono /></>}</div>;
      case "checkout": return <div className="space-y-3"><Pill k="Check-out entro" v={w.checkout?.time} /><P v={tr(w.checkout?.instructions, "checkout_instructions")} /></div>;
      case "wifi": return <div className="space-y-2"><CopyRow k="Rete" v={w.wifi?.network} ck="n" />{wifiLocked ? <Locked title="Password Wi-Fi" /> : <CopyRow k="Password" v={wifiPass} ck="p" mono />}</div>;
      case "house_rules": { const hr = w.house_rules || {}; return <div className="space-y-3">{[["smoking", "Fumo"], ["pets", "Animali"], ["noise", "Rumore"], ["parties", "Feste"], ["visitors", "Visitatori"]].map(([kk, l]) => <Info key={kk} k={l} v={hr[kk]} />)}{(hr.custom || []).map((c, i) => <Info key={i} k="Nota" v={c} />)}</div>; }
      case "parking": return <div className="space-y-3"><Info k="Disponibilità" v={w.parking?.availability} /><P v={w.parking?.instructions} /><Info k="Costo" v={w.parking?.cost} /></div>;
      case "getting_around": return <MobilityBlock guide={w} notes={tr(w.getting_around, "getting_around")} T={T} brand={brand} />;
      case "house_guide": return <div className="space-y-4">{(w.house_guide || []).map((it, i) => <div key={i} className="rounded-2xl border p-4" style={surface}>{it.photo && <img src={mediaUrl(it.photo)} alt="" className="w-full h-36 object-cover rounded-xl mb-3" />}<p className="font-semibold" style={text}>{it.name}</p><p className="text-sm mt-1 whitespace-pre-wrap" style={bodyCol}>{it.instructions}</p></div>)}</div>;
      case "local_guide": return <LocalList items={w.local_guide || []} T={T} brand={brand} />;
      case "emergency": { const e = w.emergency || {}; return <div className="space-y-3"><Call k="Numero emergenze" v={e.local_numbers} /><Call k="Host" v={e.host_contact} /><Call k="Contatto emergenza" v={e.emergency_contact} /><Info k="Ospedale" v={e.hospital} /><Info k="Farmacia" v={e.pharmacy} /></div>; }
      case "contact": { const c = w.contact || {}; return <div className="space-y-3">{c.host_name && <p style={bodyCol}>Il tuo host: <span className="font-semibold" style={text}>{c.host_name}</span></p>}<Call k="Telefono" v={c.phone} />{c.whatsapp && <a href={`https://wa.me/${c.whatsapp.replace(/[^0-9]/g, "")}`} className="flex items-center gap-2 rounded-xl bg-emerald-50 text-emerald-700 px-4 py-3 font-semibold"><Phone className="w-4 h-4" />WhatsApp</a>}{c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-2 rounded-xl border px-4 py-3 font-semibold" style={{ ...surface, color: T.text }}><Mail className="w-4 h-4" />{c.email}</a>}</div>; }
      default: return null;
    }
  };
  const P = ({ v }) => v ? <p className="text-[15px] leading-relaxed whitespace-pre-wrap" style={bodyCol}>{v}</p> : null;

  if (active) {
    const s = active;
    return (
      <div className="min-h-full" style={{ background: T.bg }}>
        {s.cover && <img src={mediaUrl(s.cover)} alt="" className="w-full h-44 object-cover" />}
        <div className="animate-fade-up px-5 pb-28 pt-4">
          <button onClick={() => setActive(null)} className="flex items-center gap-1 text-sm mb-4" style={label} data-testid="guide-back"><ChevronLeft className="w-4 h-4" />Indietro</button>
          <div className="flex items-center gap-3 mb-1"><div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: `${brand}22`, color: brand }}><Icon name={s.icon} className="w-5 h-5" /></div><h2 className="font-serif-display text-2xl leading-tight" style={text}>{s.title}</h2></div>
          {s.subtitle && <p className="text-sm mb-5 ml-14" style={label}>{s.subtitle}</p>}
          <div className="space-y-4">{structured(s)}<BlockRenderer blocks={s.blocks} T={T} brand={brand} /></div>
        </div>
        {pinOpen && (
          <div className="fixed inset-0 z-[80] bg-black/60 flex items-end md:items-center justify-center p-4" onClick={() => !pinBusy && setPinOpen(false)} data-testid="pin-modal">
            <div className="w-full max-w-sm rounded-3xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
              <div className="w-12 h-12 rounded-2xl grid place-items-center mx-auto" style={{ background: `${brand}22`, color: brand }}><Lock className="w-6 h-6" /></div>
              <h3 className="text-center font-serif-display text-xl mt-3 text-slate-900">Informazioni di accesso</h3>
              <p className="text-center text-sm text-slate-500 mt-1">Inserisci il PIN fornito dal tuo host per visualizzare i dati protetti.</p>
              <input value={pin} onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric" maxLength={8}
                placeholder="••••" data-testid="pin-input" autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") verifyPin(); }}
                className="mt-4 w-full text-center tracking-[0.4em] text-2xl font-mono rounded-2xl border border-slate-200 py-3 outline-none focus:border-blue-500" />
              {pinErr && <p className="text-center text-sm text-red-500 mt-2" data-testid="pin-error">{pinErr}</p>}
              <button onClick={verifyPin} disabled={pinBusy || pin.length < 4} data-testid="pin-submit"
                className="mt-4 w-full rounded-2xl py-3 font-semibold text-white disabled:opacity-50" style={{ background: brand }}>{pinBusy ? "Verifica…" : "Sblocca"}</button>
              <button onClick={() => setPinOpen(false)} className="mt-2 w-full text-sm text-slate-400">Annulla</button>
            </div>
          </div>
        )}
        {bottomNav && <Nav T={T} brand={brand} onHome={() => setActive(null)} sections={sections} setActive={setActive} />}
      </div>
    );
  }

  const quick = [
    byKey("wifi") && { label: "Wi-Fi", icon: "wifi", onClick: () => setActive(byKey("wifi")) },
    byKey("checkin") && { label: "Check-in", icon: "door", onClick: () => setActive(byKey("checkin")) },
    guide?.basic?.address && { label: "Come arrivare", icon: "map", href: `https://maps.google.com/?q=${encodeURIComponent(guide.basic.address)}` },
    byKey("contact") && { label: "Contatta host", icon: "chat", onClick: () => setActive(byKey("contact")) },
  ].filter(Boolean);

  return (
    <div className="min-h-full pb-24" style={{ background: T.bg }}>
      <div className="relative h-60">
        <img src={cover} alt={guide?.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(15,23,42,0.8), rgba(15,23,42,0.05))" }} />
        <div className="absolute bottom-4 left-5 right-5 text-white">
          <p className="font-script text-3xl leading-none drop-shadow">Benvenuti a</p>
          <h1 className={`${T.heading === "display" ? "font-display" : "font-serif-display"} text-2xl leading-tight drop-shadow mt-1`}>{guide?.name}</h1>
          {guide?.basic?.city && <p className="text-sm flex items-center gap-1 mt-0.5 opacity-95"><MapPin className="w-3.5 h-3.5" />{guide.basic.city}{guide.basic.country ? `, ${guide.basic.country}` : ""}</p>}
        </div>
      </div>

      {tr(guide?.welcome_message, "welcome_message") && <div className="px-5 pt-5"><p className="text-[15px] leading-relaxed whitespace-pre-wrap" style={bodyCol}>{tr(guide.welcome_message, "welcome_message")}</p></div>}

      {quick.length > 0 && (
        <div className="px-5 pt-5 grid grid-cols-4 gap-2.5">
          {quick.map((q, i) => {
            const inner = <><div className="w-11 h-11 rounded-2xl grid place-items-center mx-auto" style={{ background: `${brand}22`, color: brand }}><Icon name={q.icon} className="w-5 h-5" /></div><span className="block text-[11px] font-semibold mt-1.5 text-center leading-tight" style={text}>{q.label}</span></>;
            return q.href
              ? <a key={i} href={q.href} target="_blank" rel="noreferrer" data-testid={`quick-${q.icon}`} className="active:scale-95 transition-transform">{inner}</a>
              : <button key={i} onClick={q.onClick} className="active:scale-95 transition-transform" data-testid={`quick-${q.icon}`}>{inner}</button>;
          })}
        </div>
      )}

      <ConciergeHomeCard brand={brand} T={T} />

      {guide?.smart_journey && (
        <div className="px-5 pt-6" data-testid="smart-journey-selector">
          <div className="flex gap-1 p-1 rounded-2xl border" style={surface}>
            {PHASES.map(([k, l]) => (
              <button key={k} onClick={() => setPhase(k)} data-testid={`phase-${k}`}
                className="flex-1 text-[11px] font-semibold px-2 py-2 rounded-xl transition-colors leading-tight"
                style={phase === k ? { background: brand, color: "#fff" } : { color: T.muted }}>{l}</button>
            ))}
          </div>
        </div>
      )}

      <h3 className="px-5 pt-7 pb-1 font-serif-display text-xl" style={text}>Il tuo soggiorno</h3>
      <div className="px-5 pt-2 grid grid-cols-1 gap-3">
        {orderByPhase(sections.filter((s) => s.key !== "welcome" && s.key !== "local_guide")).map((s, i) => (
          <button key={s.id} onClick={() => setActive(s)} data-testid={`guide-card-${s.key}`}
            className="flex items-center gap-4 rounded-3xl border p-4 text-left active:scale-[0.98] transition-transform"
            style={{ ...surface, boxShadow: T.dark ? "none" : "0 1px 3px rgba(15,23,42,0.06)", animation: "fade-up 0.4s ease both", animationDelay: `${i * 35}ms` }}>
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${brand}22`, color: brand }}><Icon name={s.icon} className="w-6 h-6" /></div>
            <div className="min-w-0"><p className="font-semibold" style={text}>{s.title}</p><p className="text-xs truncate" style={label}>{s.subtitle}</p></div>
          </button>
        ))}
      </div>

      {(guide?.extras || []).length > 0 && (
        <div className="px-5 pt-8" data-testid="guest-extras">
          <h3 className="font-serif-display text-xl" style={text}>Extra e servizi</h3>
          <p className="text-sm" style={label}>Richiedibili durante il tuo soggiorno</p>
          <div className="mt-3 space-y-3">
            {guide.extras.map((e) => (
              <div key={e.id} className="rounded-2xl border p-4" style={surface} data-testid={`extra-card-${e.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0"><p className="font-semibold" style={text}>{e.title}</p>{e.description && <p className="text-sm mt-0.5 whitespace-pre-wrap" style={bodyCol}>{e.description}</p>}</div>
                  {e.price && <span className="text-sm font-bold shrink-0" style={{ color: brand }}>{e.price}</span>}
                </div>
                <button onClick={() => requestService(e)} data-testid={`request-extra-${e.id}`} className="mt-3 w-full rounded-xl py-2.5 font-semibold text-white text-sm active:scale-[0.98] transition-transform" style={{ background: brand }}>Richiedi</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {byKey("local_guide") && (guide.local_guide || []).length > 0 && (
        <div className="pt-8">
          <div className="px-5 flex items-end justify-between"><div><h3 className="font-serif-display text-xl" style={text}>{byKey("local_guide").title}</h3><p className="text-sm" style={label}>I nostri posti preferiti, scelti per te.</p></div></div>
          <div className="mt-3 flex gap-3 overflow-x-auto hide-scrollbar px-5 pb-1">
            {guide.local_guide.map((it, i) => (
              <div key={i} className="w-56 shrink-0 rounded-2xl border overflow-hidden" style={surface}>
                {it.photo && <img src={mediaUrl(it.photo)} alt="" className="w-full h-32 object-cover" />}
                <div className="p-3"><span className="text-[11px] font-bold uppercase" style={{ color: brand }}>{it.category}</span><p className="font-serif-display text-lg leading-tight" style={text}>{it.name}</p>{it.distance && <p className="text-xs mt-1" style={label}>{it.distance}</p>}</div>
              </div>
            ))}
          </div>
          <div className="px-5 pt-3"><button onClick={() => setActive(byKey("local_guide"))} className="text-sm font-semibold" style={{ color: brand }}>Scopri tutti i consigli →</button></div>
        </div>
      )}

      {guide?.tourist_tax && (
        <div className="px-5 pt-8" data-testid="guest-tourist-tax">
          <h3 className="font-serif-display text-xl" style={text}>Tassa di soggiorno</h3>
          <div className="mt-3 rounded-2xl border p-4" style={surface}>
            {guide.tourist_tax.amount != null && (
              <p className="text-[15px]" style={text}>
                <span className="font-bold" style={{ color: brand }}>{guide.tourist_tax.amount} {guide.tourist_tax.currency || "EUR"}</span>{" "}
                {({ person_night: "a persona a notte", person_stay: "a persona a soggiorno", stay: "a soggiorno" })[guide.tourist_tax.per] || "a persona a notte"}
              </p>
            )}
            <div className="mt-1 text-sm space-y-0.5" style={label}>
              {guide.tourist_tax.max_nights ? <p>Fino a {guide.tourist_tax.max_nights} notti.</p> : null}
              {guide.tourist_tax.children_free_under ? <p>Gratis sotto i {guide.tourist_tax.children_free_under} anni.</p> : null}
            </div>
            {guide.tourist_tax.notes && <p className="mt-2 text-xs" style={label}>{guide.tourist_tax.notes}</p>}
          </div>
        </div>
      )}

      <div className="px-5 pt-10 pb-3 flex items-center justify-center gap-1.5 opacity-60" data-testid="guest-powered-by">
        <span className="text-[11px]" style={label}>Powered by</span>
        <LogoSymbol className="h-4" />
        <span className="text-[11px] font-semibold" style={text}>StayGuide</span>
      </div>

      {bottomNav && <Nav T={T} brand={brand} onHome={() => setActive(null)} sections={sections} setActive={setActive} />}
    </div>
  );
}

function LocalList({ items, T, brand }) {
  return <div className="space-y-4">{items.map((it, i) => (
    <div key={i} className="rounded-2xl border overflow-hidden" style={{ background: T.surface, borderColor: T.border }}>
      {it.photo && <img src={mediaUrl(it.photo)} alt="" className="w-full h-40 object-cover" />}
      <div className="p-4"><span className="text-[11px] font-bold uppercase" style={{ color: brand }}>{it.category}{it.distance ? ` · ${it.distance}` : ""}</span><p className="font-serif-display text-lg leading-tight" style={{ color: T.text }}>{it.name}</p>{it.description && <p className="text-sm mt-1" style={{ color: T.dark ? T.muted : "#4B5563" }}>{it.description}</p>}{it.maps_link && <a href={it.maps_link} target="_blank" rel="noreferrer" className="text-xs font-semibold flex items-center gap-1 mt-2" style={{ color: brand }}>Apri sulla mappa <ExternalLink className="w-3 h-3" /></a>}</div>
    </div>
  ))}</div>;
}

function Nav({ T, brand, onHome, sections, setActive }) {
  const items = [
    { label: "Home", icon: HomeI, onClick: onHome },
    { label: "La casa", icon: HomeI, onClick: () => setActive(sections.find((s) => s.key === "checkin") || sections[1]) },
    { label: "Scopri", icon: Compass, onClick: () => setActive(sections.find((s) => s.key === "local_guide")) },
    { label: "Contatti", icon: MessageCircle, onClick: () => setActive(sections.find((s) => s.key === "contact")) },
  ];
  return (
    <div className="sticky bottom-0 left-0 right-0 border-t backdrop-blur" style={{ background: T.dark ? "rgba(20,20,24,0.9)" : "rgba(255,255,255,0.92)", borderColor: T.border, paddingBottom: "env(safe-area-inset-bottom)" }}>
      <div className="grid grid-cols-4">
        {items.map((it) => (
          <button key={it.label} onClick={it.onClick} className="flex flex-col items-center gap-0.5 py-2.5" style={{ color: T.muted }}>
            <it.icon className="w-5 h-5" /><span className="text-[10px] font-semibold">{it.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
